// @greenlight-spec: 10-security-compliance.md#local-dev-cli-session
// @greenlight-spec: 14-local-development.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0028-agent-cli-mcp-independent-client.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { PaginationQuerySchema, paginated } from '@greenlight/shared';
import { errorResponses } from './common.js';
const c = initContract();
// The pairing code travels in a POST body and never in a path, query, or
// fragment, so it stays out of browser history, Referer headers, and request
// logs (docs/10 §Local-dev CLI session).
const ApprovalCodeSchema = z
    .object({
    code: z.string().min(1).max(128),
})
    .strict();
/**
 * What the approver is told after the grant lands. `approved_at` backs the
 * post-approval record on `/cli/approve`. Every field here is the platform's own
 * account of the grant; the requester's self-reported client label and CLI
 * version stay out, because `POST /api/cli/sessions` is unauthenticated and in a
 * phishing attempt the attacker is the party that chose them.
 */
export const CliApprovalResultSchema = z.object({
    session_id: z.string().uuid(),
    app_scope: z.array(z.string().uuid()),
    expires_at: z.string().datetime({ offset: true }),
    approved_at: z.string().datetime({ offset: true }),
});
/**
 * One CLI credential — an `agent_sessions` row of CLI origin, never a
 * `cli_sessions` handshake. `sign_in_path` is a wire enum rather than the stored
 * `agent_sessions.origin`, whose `mcp` member must never appear on this surface:
 * mapping makes an MCP session unrepresentable here instead of merely filtered
 * out.
 *
 * `requester` is the client label and CLI version captured at create, present
 * only for pairing-minted rows. `POST /api/cli/sessions` is unauthenticated, so
 * the caller chose both strings — every surface that renders them must label
 * them self-reported and none may treat them as evidence.
 */
export const CliSignInSchema = z.object({
    id: z.string().uuid(),
    bound_at: z.string().datetime({ offset: true }),
    sign_in_path: z.enum(['pairing', 'loopback']),
    status: z.enum(['active', 'revoked']),
    user: z.object({
        id: z.string().uuid(),
        email: z.string(),
        display_name: z.string().nullable(),
    }),
    approval_channel: z.enum(['dashboard', 'mcp']).optional(),
    revoked_at: z.string().datetime({ offset: true }).optional(),
    requester: z
        .object({
        client_label: z.string().optional(),
        cli_version: z.string().optional(),
    })
        .optional(),
});
// `org` widens the page past the caller's own credentials and is IT-only. It is
// bound into the cursor, so a page issued under one scope cannot be replayed
// under the other (docs/16 §Filtering a paginated list).
export const CliSignInsQuerySchema = PaginationQuerySchema.extend({
    scope: z.enum(['mine', 'org']).optional(),
});
/**
 * Deliberately carries nothing about the targeted row. A caller who could tell a
 * real revoke from a no-op could probe UUIDs for existence and ownership, so
 * every outcome — revoked, unknown id, someone else's session, already revoked —
 * answers with this identical body (docs/10 §Local-dev CLI session). `status`
 * states the end state the call guarantees, not that a row changed.
 */
export const CliSignInRevokeResultSchema = z.object({
    status: z.literal('revoked'),
    message: z.string(),
});
export const cliContract = c.router({
    approve: {
        method: 'POST',
        path: '/api/cli/approve',
        body: ApprovalCodeSchema,
        responses: { 200: CliApprovalResultSchema, ...errorResponses },
        summary: 'Approve a pending CLI pairing code, signing that CLI in as the caller.',
    },
    listSignIns: {
        method: 'GET',
        path: '/api/cli/sign-ins',
        query: CliSignInsQuerySchema,
        responses: { 200: paginated(CliSignInSchema), ...errorResponses },
        summary: "List the caller's CLI credentials newest-first (cursor-paginated by bind time desc); scope=org widens to every user's, IT only.",
    },
    revokeSignIn: {
        method: 'POST',
        path: '/api/cli/sign-ins/:sessionId/revoke',
        pathParams: z.object({ sessionId: z.string().uuid() }),
        body: c.noBody(),
        responses: { 200: CliSignInRevokeResultSchema, ...errorResponses },
        summary: "Revoke one CLI credential, cascading the user's local proxy tokens. Self or IT; idempotent and identical for a row the caller cannot see.",
    },
});
//# sourceMappingURL=cli.js.map